import React from 'react';
import * as FiIcons from 'react-icons/fi';
import * as Fa6Icons from 'react-icons/fa6';
import SafeIcon from '../common/SafeIcon';

const {
  FiExternalLink,
  FiFacebook,
  FiInstagram,
  FiMessageCircle
} = FiIcons;
const { FaThreads } = Fa6Icons;

const Footer = ({ lang }) => {
  const isChinese = lang === 'zh-TW';
  const chineseClass = isChinese ? '' : 'hidden';
  const englishClass = isChinese ? 'hidden' : '';

  return (
    <footer className="w-full mt-32 pb-20 px-6 border-t border-slate-100 bg-white/50">
      <div className="max-w-6xl mx-auto pt-16">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12">
          <section
            id="about-emoji-glint"
            aria-labelledby="about-heading"
            className="scroll-mt-24"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center p-2 shadow-xl shadow-slate-200">
                <svg
                  viewBox="0 0 100 100"
                  className="w-full h-full"
                  aria-hidden="true"
                >
                  <path
                    d="M50 10 L58 42 L90 50 L58 58 L50 90 L42 58 L10 50 L42 42 Z"
                    fill="white"
                  />
                </svg>
              </div>
              <span className="text-2xl font-black text-slate-800 tracking-tighter">
                glint.homy.hk
              </span>
            </div>

            <h2
              id="about-heading"
              className="text-xl font-black text-slate-800 mb-3"
            >
              <span className={chineseClass}>
                Emoji Glint 是誰打造的？
              </span>
              <span className={englishClass}>
                Who is behind Emoji Glint?
              </span>
            </h2>

            <p className="text-slate-500 text-sm max-w-xl leading-relaxed font-medium">
              {isChinese
                ? 'Emoji Glint 由 Homy Tech Limited 開發及維護，運用 AI 語意技術，協助創作者、社群小編與提示詞工程師快速找到精準的 Emoji 和符號。'
                : 'Emoji Glint is developed and maintained by Homy Tech Limited. It uses semantic AI to help creators, social media teams and prompt engineers find precise emojis and symbols.'}
            </p>

            <a
              href="https://homy.hk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sm font-black text-indigo-600 hover:text-indigo-700 transition-colors"
            >
              {isChinese ? '認識 Homy Tech Limited' : 'Meet Homy Tech Limited'}
              <SafeIcon icon={FiExternalLink} className="text-sm" />
            </a>
          </section>

          <section
            id="contact-emoji-glint"
            aria-labelledby="contact-heading"
            className="scroll-mt-24 lg:border-l lg:border-slate-100 lg:pl-12"
          >
            <h2
              id="contact-heading"
              className="text-xl font-black text-slate-800 mb-3"
            >
              <span className={chineseClass}>
                如何聯絡 Emoji Glint 團隊？
              </span>
              <span className={englishClass}>
                How can you contact the Emoji Glint team?
              </span>
            </h2>

            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              {isChinese
                ? '歡迎透過 Homy Tech 官方網站或以下官方社群平台與團隊聯絡。'
                : 'Contact the team through the Homy Tech website or one of the official social channels below.'}
            </p>

            <address className="not-italic">
              <a
                href="https://homy.hk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mb-6 text-sm font-black text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                <SafeIcon icon={FiMessageCircle} className="text-base" />
                {isChinese ? '前往官方網站聯絡我們' : 'Contact us on our website'}
                <SafeIcon icon={FiExternalLink} className="text-sm" />
              </a>

              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/homy.hk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center w-11 h-11 rounded-full bg-slate-100 hover:bg-gradient-to-br hover:from-purple-500 hover:via-pink-500 hover:to-orange-400 transition-all duration-300"
                  aria-label="Contact Homy Tech on Instagram"
                >
                  <SafeIcon
                    icon={FiInstagram}
                    className="text-slate-600 group-hover:text-white text-lg transition-colors"
                  />
                </a>

                <a
                  href="https://www.threads.com/@homy.hk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center w-11 h-11 rounded-full bg-slate-100 hover:bg-slate-900 transition-all duration-300"
                  aria-label="Contact Homy Tech on Threads"
                >
                  <SafeIcon
                    icon={FaThreads}
                    className="text-slate-600 group-hover:text-white text-lg transition-colors"
                  />
                </a>

                <a
                  href="https://www.facebook.com/profile.php?id=61578745554661"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center w-11 h-11 rounded-full bg-slate-100 hover:bg-blue-600 transition-all duration-300"
                  aria-label="Contact Homy Tech on Facebook"
                >
                  <SafeIcon
                    icon={FiFacebook}
                    className="text-slate-600 group-hover:text-white text-lg transition-colors"
                  />
                </a>
              </div>
            </address>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© {new Date().getFullYear()} Homy Tech Limited</span>
          <nav
            aria-label={isChinese ? '公司資訊' : 'Company information'}
            className="flex items-center gap-4"
          >
            <a
              href="#about-emoji-glint"
              className="hover:text-indigo-600 transition-colors"
            >
              {isChinese ? '關於我們' : 'About'}
            </a>
            <a
              href="#contact-emoji-glint"
              className="hover:text-indigo-600 transition-colors"
            >
              {isChinese ? '聯絡我們' : 'Contact'}
            </a>
          </nav>
          <span>
            {isChinese
              ? 'AI 語意 Emoji 與符號搜尋工具'
              : 'AI semantic emoji and symbol search'}
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;